import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import {
  Avatar,
  FormControl,
  InputLabel,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Select
} from '@mui/material';

import { ResidentCloseConnectionModel } from '@/models/ResidentCloseConnectionModel';
import {
  CrmLeadInfoOriginInboundType,
  CrmLeadInfoOriginOutboundType
} from '@/types/crm-lead-info';

import { AddProspectResidentFormData } from './AddProspectResidentForm';
import { showCreateCloseConnectionDialog } from './CreateCloseConnectionDialog';

interface LeadOriginTypeOption {
  id: CrmLeadInfoOriginInboundType | CrmLeadInfoOriginOutboundType;
  label: string;
}

// Origin inbound + outbound types are the same for now
export const originTypeOptions: LeadOriginTypeOption[] = [
  {
    id: CrmLeadInfoOriginInboundType.PROSPECTIVE_RESIDENT,
    label: 'Prospective Resident'
  },
  {
    id: CrmLeadInfoOriginInboundType.RESIDENT_CLOSE_CONNECTION,
    label: 'Resident Family Or Friend'
  },
  {
    id: CrmLeadInfoOriginInboundType.REFERRAL_SOURCE,
    label: 'Referral Source'
  },
  {
    id: CrmLeadInfoOriginInboundType.REFERRAL_SOURCE_COMPANY,
    label: 'Referral Source Company'
  }
];

interface SelectOriginTypeProps {
  formMethods: UseFormReturn<AddProspectResidentFormData>;
  label: 'Who reached out?' | 'Outbound Recipient';
  name:
    | 'crm_lead_info.origin_inbound_type'
    | 'crm_lead_info.origin_outbound_type';
}

/**
 *
 * This is a highly complex component that at first displays only three options
 * ie Prospect Resident, Resident Close Connection, Referral Source. However, when
 * the user selects Resident Close Connection, a dialog is shown to create a new
 * Resident Close Connection. Once the user creates a new Resident Close Connection,
 * the component will display the Resident Close Connection's name and
 * relationship. The Close Connection isn't created immediately because it has
 * a foreign key constraint on the resident - thus both are created via API.
 */
export const SelectOriginType: React.FC<SelectOriginTypeProps> = ({
  formMethods,
  name,
  label
}) => {
  const closeConnection = formMethods.watch('crm_lead_info.close_connection');

  const renderOriginType = (
    value: CrmLeadInfoOriginInboundType | CrmLeadInfoOriginOutboundType | null
  ): React.ReactNode => {
    if (
      value === CrmLeadInfoOriginOutboundType.RESIDENT_CLOSE_CONNECTION &&
      !(closeConnection instanceof ResidentCloseConnectionModel)
    ) {
      // Don't render anything if the user exited the dialog without creating a
      // Close Connection
      return;
    } else if (
      value === CrmLeadInfoOriginOutboundType.RESIDENT_CLOSE_CONNECTION &&
      closeConnection instanceof ResidentCloseConnectionModel
    ) {
      return (
        <ListItem
          disableGutters
          disablePadding
          sx={{
            '& .MuiListItemAvatar-root': { minWidth: 0, mr: 1 },
            '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '16px' },
            '& .MuiListItemText-root': { mt: 0, mb: 0 },
            '& .MuiListItemText-primary': {
              fontWeight: 500,
              fontSize: '14px'
            },
            '& .MuiListItemText-secondary': {
              fontSize: '14px',
              color: '#667A86'
            }
          }}
        >
          <ListItemAvatar>
            <Avatar>{closeConnection.initials}</Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={closeConnection.fullName}
            secondary={closeConnection.relationship}
          />
        </ListItem>
      );
    }
    return originTypeOptions?.find((option) => option?.id === value)?.label;
  };

  const handleOnChange = async (
    onFieldChange: (...event: any[]) => void,
    value: CrmLeadInfoOriginInboundType | CrmLeadInfoOriginOutboundType
  ) => {
    switch (value) {
      case CrmLeadInfoOriginInboundType.RESIDENT_CLOSE_CONNECTION: {
        try {
          const { closeConnection } = await showCreateCloseConnectionDialog();
          formMethods.setValue(
            'crm_lead_info.close_connection',
            closeConnection
          );
          onFieldChange(value);
        } catch (error) {
          onFieldChange(null);
        }
        break;
      }
      case CrmLeadInfoOriginInboundType.PROSPECTIVE_RESIDENT:
      case CrmLeadInfoOriginInboundType.REFERRAL_SOURCE:
      default: {
        onFieldChange(value);
        break;
      }
    }
  };

  return (
    <Controller
      control={formMethods.control}
      name={name}
      render={({
        field: { onChange: onFieldChange, ...field },
        fieldState
      }) => (
        <FormControl fullWidth error={!!fieldState.error}>
          <InputLabel>{label}</InputLabel>
          {/* @ts-expect-error */}
          <Select
            sx={{
              '& .MuiSelect-select':
                closeConnection instanceof ResidentCloseConnectionModel
                  ? { padding: '7px 14px' }
                  : {}
            }}
            renderValue={renderOriginType}
            label={label}
            {...field}
          >
            {originTypeOptions.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                onClick={() => {
                  handleOnChange(onFieldChange, option.id);
                }}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
};
